/**
 * @file     analytics.ts
 * @purpose  Canonical string constants for all analytics event names used across the app, plus the AnalyticsEventName type.
 */
export const analyticsEvents = {
  pageViewed: 'page_viewed',
  sectionViewed: 'section_viewed',
  scrollMilestone: 'scroll_milestone',
  leadFormSubmitted: 'lead_form_submitted',
  formStarted: 'form_started',
  formFieldInteracted: 'form_field_interacted',
  newsletterSignup: 'newsletter_signup',
  showingRequestStarted: 'showing_request_started',
  navItemClicked: 'nav_item_clicked',
  outboundLinkClicked: 'outbound_link_clicked',
  contactMethodClicked: 'contact_method_clicked',
  listingCardClicked: 'listing_card_clicked',
  blogOpen: 'blog_open',
  blogCardView: 'blog_card_view',
  blogFeaturedView: 'blog_featured_view',
  leadFlowStarted: 'lead_flow_started',
  stepCompleted: 'step_completed',
  dealResultsViewed: 'deal_results_viewed',
  leadSubmitted: 'lead_submitted',
  dealFinderCompleted: 'deal_finder_completed',
  academyLessonStarted: 'academy_lesson_started',
  academyLessonCompleted: 'academy_lesson_completed',
  academyQuizAttempted: 'academy_quiz_attempted',
  academyQuizPassed: 'academy_quiz_passed',
  academyStreakExtended: 'academy_streak_extended',
  academyBadgeEarned: 'academy_badge_earned',
  academyLockedCtaViewed: 'academy_locked_cta_viewed',
  academySignupFromGate: 'academy_signup_from_gate',
  academyIntakeStarted: 'academy_intake_started',
  academyIntakeStepCompleted: 'academy_intake_step_completed',
  academyIntakeCompleted: 'academy_intake_completed',
  academyIntakeAbandoned: 'academy_intake_abandoned',
  // Property detail data panels (impressions)
  propertyMyReadViewed: 'property_my_read_viewed',
  propertyMarketStoryViewed: 'property_market_story_viewed',
  propertySupplyOutlookViewed: 'property_supply_outlook_viewed',
  propertyAreaMixViewed: 'property_area_mix_viewed',
  propertyDemographicsViewed: 'property_demographics_viewed',
  // Listings sign-up gate (view a listing detail → register)
  listingsGateShown: 'listings_gate_shown',
  listingsGateContinue: 'listings_gate_continue',
  listingsGateIntentSelected: 'listings_gate_intent_selected',
  // Map data-overlay pills (transit / schools / crime / density)
  mapOverlayToggle: 'map_overlay_toggle',
  // Property detail interactions
  propertyMyReadLensSwitched: 'property_my_read_lens_switched',
  propertyMyReadBreakdownExpanded: 'property_my_read_breakdown_expanded',
  propertyMarketStoryModeSwitched: 'property_market_story_mode_switched',
  propertyDemographicsTabSwitched: 'property_demographics_tab_switched',
  propertyDetailViewed: 'property_detail_viewed',
  propertyDetailClosed: 'property_detail_closed',
  propertyDetailTabChanged: 'property_detail_tab_changed',
  propertyShowingRequestOpened: 'property_showing_request_opened',
  propertyShowingRequestSubmitted: 'property_showing_request_submitted',
  propertyShowingRequestAbandoned: 'property_showing_request_abandoned',
  propertyDocumentRequestOpened: 'property_document_request_opened',
  propertyDocumentRequestSubmitted: 'property_document_request_submitted',
  propertyDocumentRequestAbandoned: 'property_document_request_abandoned',
  propertyGateViewed: 'property_gate_viewed',
  propertyGateUnlockClicked: 'property_gate_unlock_clicked',
  // Offer-attempt funnel
  propertyOfferAttemptOpened: 'property_offer_attempt_opened',
  propertyOfferAttemptStepChanged: 'property_offer_attempt_step_changed',
  propertyOfferAttemptSubmitted: 'property_offer_attempt_submitted',
  propertyOfferAttemptFailed: 'property_offer_attempt_failed',
  // Official App Store / Google Play badge links. metadata: { store: 'app_store' | 'google_play', location: 'footer' | 'app_prompt' }
  appStoreBadgeClicked: 'app_store_badge_clicked',
} as const;

// Visitor profile events (Journey System, J003)
export const PROFILE_DATA_COLLECTED = 'profile_data_collected';
export const DEAL_FINDER_PREFILLED = 'deal_finder_prefilled';

/**
 * profile_data_collected, fired when a visitor answers a micro-interaction that writes to the visitor profile
 * eventType: 'profile', category: 'visitor_profile'
 * metadata: { field: string, value: string, source_page: string, interaction_type: string }
 *
 * deal_finder_prefilled, fired when the deal finder displays a pre-filled value from visitor profile
 * eventType: 'profile', category: 'visitor_profile'
 * metadata: { field: string, source_page: string, user_confirmed: boolean }
 */

export type AnalyticsEventName =
  | (typeof analyticsEvents)[keyof typeof analyticsEvents]
  | (string & {});
